<template>
  <div id="page-product-form">
    <vs-row>
      <vs-col>
        <vs-row>
          <vs-col>
            <card-page card-title="Report">
              <vue-element-loading :active="busy"></vue-element-loading>
              <vue-form-generator
                  ref="form"
                  :model="model"
                  :options="formOptions"
                  :schema="schema"
                  :class="readonly ? 'readonly-form py-4' : 'py-4'"
              />
            </card-page>

            <card-page>
              <grid-panel
                  ref="recipients"
                  :auto-size="true"
                  :column-defs="columnDefs"
                  :data="recipients"
                  :show-search="false"
                  height="short"
                  :loading="gridLoading"
                  @update:gridApi="(gridApi) => this.gridApi = gridApi"
              >
                <vs-dropdown-item v-if="!readonly">
                  <span
                      class="flex items-center"
                      @click="removeRecipients"
                  >
                    <feather-icon icon="MinusIcon" svgClasses="h-4 w-4" class="mr-2" />
                    <span>Remove Recipients</span>
                  </span>
                </vs-dropdown-item>
              </grid-panel>
            </card-page>

            <card-page card-title="Add Member" v-if="!readonly && !hideAddMember">
              <div class="flex">
                <vue-form-generator
                    class="w-1/4 py-4"
                    ref="type-form"
                    :model="recipientsModel"
                    :options="formOptions"
                    :schema="typeSchema"
                />
                <vue-form-generator
                    v-show="this.emailTypeState === 'Contact'"
                    class="w-3/4 py-4"
                    ref="contact-form"
                    :model="recipientsModel"
                    :options="formOptions"
                    :schema="contactEmailSchema"
                />
                <vue-form-generator
                    v-show="this.emailTypeState === 'Entity'"
                    class="w-3/4 py-4"
                    ref="company-form"
                    :model="recipientsModel"
                    :options="formOptions"
                    :schema="companyEmailSchema"
                />
              </div>
            </card-page>
          </vs-col>
        </vs-row>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from '@/components/mixins/FormModelMixin';
import {selectOptions} from '@/components/form/FormElementOption';
import GridPanel from '@/views/components/ag-grid/GridPanel';
import NotificationMixin from '@/components/mixins/NotificationMixin';
import CardPage from '../../../components/Views/CardPage';
import FormFieldValidationMixin from '../../../../components/mixins/FormFieldValidationMixin';

const DistributionTypeId = 57;

export default {
  name: 'DistributionListForm',
  components: {
    GridPanel,
    CardPage,
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    disallowedReportTypes: {
      type: Array,
      required: false,
      default: () => [],
    },
    hideAddMember: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  mixins: [FormModelMixin, NotificationMixin, FormFieldValidationMixin],
  data() {
    return {
      gridApi: null,
      model: {
        ReportType_FK: null,
        Company_FK: {},
      },
      recipientsModel: {
        company: {},
        contact: {},
        emailType: null,
      },
      emailTypeState: null,
      schema: {
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/4 pb-0 mb-0',
                model: 'ReportType_FK',
                values: () => this.reportTypes,
                selectOptions,
                required: true,
                validator: 'required',
              },
            ]
          },
        ],
      },
      typeSchema:{
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-full',
                label: 'Type',
                model: 'emailType',
                values: [
                  {id: 0, name: 'Entity'},
                  {id: 1, name: 'Contact'},
                ],
                onChanged: async (model) => {
                  await this.setDefaultCompany(model.emailType.id);
                },
                selectOptions: {
                  ...selectOptions,
                  loading: false,
                },
              },
            ]
          },
        ]
      },

      companyEmailSchema: {
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3 mb-0',
                label: 'Entity Name',
                model: 'company.Company_FK',
                values: () => this.companies,
                selectOptions,
                validator: async (value, field, model) => {
                  const company = model.company
                  this.companyEmailSchema.groups[0].fields[1].selectOptions.loading = true;

                  delete this.recipientsModel.company.Email_FK
                  await this.loadCompanyEmailAddresses(company.Company_FK.id);

                  this.companyEmailSchema.groups[0].fields[1].selectOptions.loading = false;
                },
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3 mb-0',
                label: 'Entity Email Address',
                model: 'company.Email_FK',
                values: () => this.companyEmailAddresses.filter(({id}) => !this.usedRecipientIds.includes(id)),
                selectOptions: {
                  ...selectOptions,
                  loading: false,
                },
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: ({company}) => this.gridLoading || !company.Email_FK|| this.addingRecipient,
                type: 'button',
                styleClasses: 'w-full flex justify-end',
                buttonText: 'Add',
                onClick: async ({company}, schema) => {
                  await this.addRecipient(company);
                }
              },
            ]
          },
        ],
      },
      contactEmailSchema: {
        groups: [
          {
            styleClasses: 'w-full flex flex-wrap justify-end',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3 mb-0',
                label: 'Entity Name',
                model: 'contact.Company_FK',
                values: () => this.companies,
                selectOptions,
                onChanged: async ({contact}) => {
                  const options = this.contactEmailSchema.groups[0].fields[1].selectOptions;
                  options.loading = true;

                  delete this.recipientsModel.contact.Contact_FK;
                  await this.loadContacts(contact.Company_FK.id);

                  options.loading = false;
                },
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3 mb-0',
                label: 'Contact Name',
                model: 'contact.Contact_FK',
                values: () => this.contacts,
                selectOptions: {
                  ...selectOptions,
                  loading: false,
                },
                onChanged: async ({contact}) => {
                  const options = this.contactEmailSchema.groups[0].fields[2].selectOptions;
                  options.loading = true;

                  delete this.recipientsModel.contact.Email_FK;
                  await this.loadContactEmailAddresses(contact.Contact_FK.id);

                  options.loading = false;
                },
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3 mb-0',
                label: 'Contact Email Address',
                model: 'contact.Email_FK',
                values: () => this.contactEmailAddresses
                    .filter(({id}) => !this.usedContactRecipientIds.includes(id)),
                selectOptions: {
                  ...selectOptions,
                  loading: false,
                },
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: ({contact}) => this.gridLoading || !contact.Email_FK || this.addingRecipient,
                type: 'button',
                styleClasses: 'w-full flex justify-end',
                buttonText: 'Add',
                selectOptions: {
                  ...selectOptions,
                  loading: false,
                },
                onClick: async ({contact}, schema) => {
                  await this.addRecipient(contact);
                }
              },
            ]
          },
        ],
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      url: null,
      columnDefs: [
        {
          checkboxSelection: !this.readonly,
          headerName: 'Email',
          width: 350,
          valueGetter: ({data}) => {
            const {company_email, contact_email} = data;
            const Email_FK = parseInt(data.Email_FK);

            const companyEmailId = company_email ? company_email.CompanyEmailAddress_PK : null;
            const userContactEmailId = contact_email ? contact_email.ContactEmailAddress_PK : null;

            if (userContactEmailId === Email_FK) {
              return contact_email.EmailAddress;
            }

            if (companyEmailId === Email_FK) {
              return company_email.EmailAddress;
            }

            return '';
          },
        },
        {
          headerName: 'Name',
          width: 350,
          valueGetter: ({data}) => {
            const {company, contact} = data;

            if (contact) {
              return contact.FirstName + ' ' + contact.LastName;
            }

            if (company) {
              return company.CompanyName;
            }

            return '';
          },
        },
      ],
      reportTypes: [],
      companies: [],
      contacts: [],
      companyEmailAddresses: [],
      contactEmailAddresses: [],
      options: [
        {
          endpoint: () => this.$api.companies.distributionLists.types.list(),
          optionsFieldName: 'reportTypes',
          nameFieldName: 'Type',
          idFieldName: 'ReportType_FK',
        },
        {
          endpoint: () => this.$api.companies.list(),
          optionsFieldName: 'companies',
          nameFieldName: 'CompanyName',
          idFieldName: 'Company_FK',
        },
      ],
      recipients: [],
      gridLoading: false,
      reportId: null,
      addingRecipient: false,
    };
  },
  computed: {
    usedRecipientIds() {
      return this.recipients.map(({Email_FK}) => parseInt(Email_FK));
    },
    usedContactRecipientIds() {
      return this.recipients.filter(({contact_email}) => contact_email)
          .map(({Email_FK}) => parseInt(Email_FK));
    },
  },
  watch: {
    'recipientsModel.emailType'(newValue, oldValue) {
      if (newValue) {
        this.emailTypeState = newValue.name;
      }
    },
    'model.id'(newValue, oldValue) {
      if (newValue) {
        this.refreshRecipients();
      }
    },
    // companies(newValues) {
    //   if (newValues.length > 0) {
    //     this.setDefaultCompany();
    //   }
    // }
  },
  methods: {
    async setDefaultCompany(type) {
      const companyId = this.$route.params.id;
      const company = this.companies.find(({id}) => id === Number(companyId));

      this.$set(this.recipientsModel.company, 'Company_FK', company);
      this.$set(this.recipientsModel.contact, 'Company_FK', company);

      if (type === 0) {
        this.validateField('company.Company_FK', 'company-form');
      }

      if (type === 1) {
        this.validateField('contact.Company_FK', 'contact-form');
      }

      await Promise.all([
        this.loadContacts(companyId),
        this.loadCompanyEmailAddresses(companyId),
      ]);
    },
    async loadContacts(companyId) {
      await this.loadOption(
          () => this.$api.companies.relationship('contacts', companyId),
          'contacts',
          'Name'
          // ({FirstName, LastName}) => FirstName + ' ' + LastName,
          // ['FirstName', 'LastName']
      );
    },
    async loadCompanyEmailAddresses(companyId) {
      await this.loadOption(
          () => this.$api.companies.relationship('emailAddresses', companyId, {EmailType_FK: DistributionTypeId}),
          'companyEmailAddresses',
          'EmailAddress'
      );
    },
    async loadContactEmailAddresses(contactId) {
      await this.loadOption(
          () => this.$api.users.relationship('emailAddresses', contactId, {EmailType_FK: DistributionTypeId}),
          'contactEmailAddresses',
          'EmailAddress'
      );
    },
    async refreshRecipients() {
      this.gridLoading = true;
      try {
        const {data} = await this.$api.companies.distributionLists.recipients(this.model.id);
        this.recipients = data.data;
      } catch (e) {
        console.log(e);
      } finally {
        this.gridLoading = false;
      }
    },
    resetCompany() {
      this.recipientsModel.company.Email_FK = null;
    },
    resetContact() {
      this.recipientsModel.contact.Contact_FK = null;
      this.recipientsModel.contact.Email_FK = null;
    },
    async addRecipient({Company_FK, Contact_FK, Email_FK}) {
      if (!this.model.id) {
        return;
      }

      const getReferenceId = (obj) => {
        if (!obj) return null;

        return obj.id;
      };

      this.gridLoading = true;
      const recipient = {
        Company_FK: getReferenceId(Company_FK),
        Contact_FK: getReferenceId(Contact_FK),
        Email_FK: getReferenceId(Email_FK),
      };

      try {
        this.addingRecipient = true;
        await this.$api.companies.distributionLists.update(this.model.id, {recipient});

        if (Contact_FK) {
          this.resetContact();
        } else {
          this.resetCompany();
        }

        this.notifySuccess('Recipient added');

        this.recipientsModel.company.Email_FK = null;
        this.recipientsModel.contact.Email_FK = null;
        await this.refreshRecipients();
      } catch (e) {
        console.log(e);
        this.notifyError('Recipient failed to be added. Please try again.');
      } finally {
        this.gridLoading = false;
        this.addingRecipient = false;
      }
    },
    async removeRecipients() {
      const selected = this.gridApi.getSelectedNodes();

      console.log(selected);

      if (selected.length === 0) {
        this.notifyError('Please select one or more recipients from the grid');
        return;
      }

      this.gridLoading = true;

      const recipientIds = selected.map(({data}) => data.id);

      try {
        await this.$api.companies.distributionLists.removeRecipients(this.model.id, recipientIds);

        this.notifySuccess('Recipient removed');

        await this.refreshRecipients();
      } catch (e) {
        console.log(e);
        this.notifyError('Recipient failed to be removed. Please try again.');
      } finally {
        this.gridLoading = false;
      }
    }
  },
};
</script>

<style lang="scss">

</style>
